<template>
  <div
    class="chart-container"
    :class="{ 'chart-container--db-bubbles': showDbBubbles && chartBubbleItems.length }"
  >
    <div ref="chartRef" class="chart echarts-chart"></div>
    <div
      v-if="showDbBubbles && chartBubbleItems.length"
      class="chart-db-bubbles"
    >
      <div
        v-for="item in chartBubbleItems"
        :key="'db-' + item.number"
        class="chart-db-bubble-col"
      >
        <div class="db-bubble-slot db-bubble-slot--up">
          <span
            v-if="item.upText"
            class="db-bubble"
            :style="{ backgroundColor: item.upBg }"
          >{{ item.upText }}</span>
        </div>
        <div class="db-bubble-slot db-bubble-slot--down">
          <span
            v-if="item.downText"
            class="db-bubble"
            :class="{ 'db-bubble--change': item.downChangeStyle && Object.keys(item.downChangeStyle).length }"
            :style="{ backgroundColor: item.downBg, ...item.downChangeStyle }"
          >{{ item.downText }}</span>
        </div>
      </div>
    </div>
    <div class="slider-container">
      <div class="slider-wrapper">
        <div class="time-display">{{ timeDisplay }}</div>
        <button 
          class="nav-button" 
          @click="handlePrev" 
          :disabled="!canMovePrev || isOvernightActive"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M15 18l-6-6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
        <div class="time-slider">
          <div class="slider-track">
            <div 
              class="slider-track-inactive" 
              :style="{ width: `${sliderInactiveWidthPct}%` }"
            ></div>
            <div 
              class="slider-track-active"
              :style="{ 
                left: `${sliderInactiveWidthPct}%`,
                width: `${sliderActiveWidthPct}%`
              }"
            ></div>
          </div>
          <input 
            type="range" 
            :min="0" 
            :max="sliderMaxIndex" 
            :value="sliderIndex" 
            @input="handleSliderChange"
            class="slider"
            :disabled="isOvernightActive"
          />
        </div>
        <button 
          class="nav-button" 
          @click="handleNext" 
          :disabled="!canMoveNext || isOvernightActive"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M9 18l6-6-6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
        <button 
          class="overnight-button" 
          :class="{ 'active': isOvernightActive }"
          @click="handleOvernight"
        >
          隔夜
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts';
import {
  timeKeyForMinute,
  collectAvailableTimePoints,
  minViewMinute as getMinViewMinute,
  maxViewMinute as getMaxViewMinute,
  clampViewMinute as clampViewMinuteLogic,
  defaultViewMinuteOnEntry,
  reconcileCurrentMinute,
  inferRaceViewXSyncMinute,
  raceViewXEntryMinute,
  formatTimelineMinuteLabel,
  minuteIndex,
  minutesEqual,
} from '../src/timelineLogic.js';
import { timelineDebug } from '../src/timelineDebug.js';
import { buildChartDbBubbleItem } from '../src/chartDbOdds.js';

export default {
  name: 'HorseChart',
  emits: ['view-minute-change'],
  props: {
    data: {
      type: Array,
      required: true
    },
    current_minute: {
      type: Number,
      default: -1
    },
    /** 首次进入本场时的最左刻度（父组件 firstBoot 写入，刷新不变） */
    entry_minute: {
      type: Number,
      default: null,
    },
    /** 滑块刻度（getview.time_points = race_view_x DISTINCT min） */
    time_points: {
      type: Array,
      default: null,
    },
    view_source: {
      type: String,
      default: 'live',
    },
    race_completed: {
      type: Boolean,
      default: false,
    },
    race_id: {
      type: [String, Number],
      default: ''
    },
    /** 测试直播 getview_test：首次进入定位到 current_minute（15~20） */
    testLiveBoot: {
      type: Boolean,
      default: false,
    },
    pluginEmbed: {
      type: Boolean,
      default: false,
    },
    compactLayout: {
      type: Boolean,
      default: false,
    },
    highlightPicks: {
      type: Array,
      default: () => [],
    },
    highlightBankers: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      chart: null,
      view_minute: -1,
      isOvernightActive: false,
      userHasScrubbedTimeline: false,
      /** 跟播：滑到最左或入场时开启，view_minute 跟 race_view_x 同步缘 */
      isSyncFollowMode: false,
      /** 上次刷新时的直播缘，用于判断「跟播中」时 live 前进是否继续跟 */
      lastLiveMinute: null,
      chartBubbleItems: [],
      bootstrappedRaceId: '',
      /** 每场仅应用一次进入默认分钟；布局/resize 不得改时间 */
      entryMinuteApplied: false,
      /** 仅 windata_tyq_refresh / 轮询刷新后为 true；入场加载禁止跟播 */
      liveFollowEnabled: false,
      chartUpdating: false,
      chartReady: false,
      lastResizeW: 0,
      lastResizeH: 0,
    }
  },
  computed: {
    showDbBubbles() {
      return this.pluginEmbed || this.compactLayout;
    },
    /** 跟播缘 = getview.current_minute（race_view_x MIN(min)） */
    liveMinute() {
      return reconcileCurrentMinute(this.current_minute, this.data, {
        viewSource: this.view_source,
        raceCompleted: this.isRaceCompleted,
      });
    },
    timeDisplay() {
      if (this.isOvernightActive) {
        return '隔夜';
      }
      return formatTimelineMinuteLabel(this.view_minute);
    },
    availableTimePoints() {
      if (Array.isArray(this.time_points) && this.time_points.length) {
        return this.time_points.map((p) => Number(p));
      }
      if (!this.data?.length) return [];
      return collectAvailableTimePoints(this.data);
    },
    sliderMaxIndex() {
      return Math.max(0, this.availableTimePoints.length - 1);
    },
    sliderIndex() {
      return minuteIndex(this.availableTimePoints, this.view_minute);
    },
    sliderInactiveWidthPct() {
      const maxIdx = this.sliderMaxIndex;
      if (maxIdx <= 0) return 0;
      const liveIdx = minuteIndex(this.availableTimePoints, this.liveMinute);
      return Math.max(0, Math.min(100, (liveIdx / maxIdx) * 100));
    },
    sliderActiveWidthPct() {
      const maxIdx = this.sliderMaxIndex;
      if (maxIdx <= 0) return 100;
      const liveIdx = minuteIndex(this.availableTimePoints, this.liveMinute);
      return Math.max(0, Math.min(100, ((maxIdx - liveIdx) / maxIdx) * 100));
    },
    minViewMinute() {
      return getMinViewMinute(this.availableTimePoints);
    },
    maxViewMinute() {
      return getMaxViewMinute(this.availableTimePoints);
    },
    isRaceCompleted() {
      return !!this.race_completed;
    },
    canMovePrev() {
      return minuteIndex(this.availableTimePoints, this.view_minute) > 0;
    },
    canMoveNext() {
      const points = this.availableTimePoints;
      const idx = minuteIndex(points, this.view_minute);
      return idx >= 0 && idx < points.length - 1;
    }
  },
  methods: {
    initChart() {
      const chartDom = this.$refs.chartRef;
      if (!chartDom) return;
      chartDom.style.width = '100%';
      chartDom.style.minWidth = '0';
      chartDom.style.maxWidth = '100%';
      if (this.chart) {
        this.chart.dispose();
        this.chart = null;
      }
      const initW = Math.max(chartDom.clientWidth || 0, 280);
      const capH = parseInt(chartDom.getAttribute('data-plugin-max-h'), 10);
      const initH = Math.max(capH || parseInt(chartDom.style.height, 10) || 0, 160);
      this.chart = echarts.init(chartDom, null, {
        renderer: 'canvas',
        width: initW,
        height: initH,
      });
      this.chartReady = false;
      window.addEventListener('resize', this.handleResize);
      this.$nextTick(() => {
        this.updateChartData();
        this.$nextTick(() => this.resizeChart());
      });
    },

    getChartSize() {
      const el = this.$refs.chartRef;
      if (!el) return null;
      let w = el.clientWidth;
      let h = el.clientHeight;
      if (this.pluginEmbed || this.compactLayout) {
        const capAttr = el.getAttribute('data-plugin-max-h');
        const styleH = parseInt(el.style.height, 10);
        const parentH = el.parentElement?.clientHeight;
        h = el.clientHeight > 48 ? el.clientHeight : (styleH > 0 ? styleH : 0);
        if (h < 48 && capAttr) h = parseInt(capAttr, 10) || 0;
        const bubbleReserve = this.showDbBubbles ? 52 : 0;
        if (h < 48 && parentH > 48) h = parentH - 40 - bubbleReserve;
        if (h < 48 && capAttr) h = parseInt(capAttr, 10) || 140;
      } else if (h < 80) {
        h = el.parentElement?.clientHeight || Math.max(160, Math.floor((window.innerHeight || 600) * 0.28));
      }
      if (w < 40) w = el.parentElement?.clientWidth || window.innerWidth || 390;
      w = Math.round(w);
      h = Math.round(h);
      if (w < 48 || h < 48) return null;
      return { w, h };
    },

    hasChartSeries() {
      if (!this.chart) return false;
      try {
        const opt = this.chart.getOption();
        const series = opt?.series;
        return Array.isArray(series) ? series.length > 0 : !!series;
      } catch (e) {
        return false;
      }
    },

    resizeChart() {
      if (!this.chart || this.chartUpdating || !this.chartReady) return;
      if (!this.hasChartSeries()) return;
      const size = this.getChartSize();
      if (!size) return;
      const { w, h } = size;
      if (w === this.lastResizeW && h === this.lastResizeH) return;
      try {
        this.chart.resize({ width: w, height: h });
        this.lastResizeW = w;
        this.lastResizeH = h;
      } catch (err) {
        this.chartReady = false;
        timelineDebug('resize_fail', { msg: err?.message, w, h });
      }
    },

    handleResize() {
      this.resizeChart();
    },

    /** 布局完成后由 viewdata 调用：直播刷新时先跟频谱缘，再重绘柱图 */
    refreshChart() {
      if (!this.chart) {
        this.initChart();
        return;
      }
      if (!this.entryMinuteApplied && this.data?.length) {
        this.tryApplyEntryMinute();
      } else if (this.liveFollowEnabled && !this.userHasScrubbedTimeline) {
        this.followLiveAfterRefresh();
      }
      this.updateChartData();
      this.$nextTick(() => {
        requestAnimationFrame(() => this.resizeChart());
      });
    },

    getChartLayoutMetrics(chartDataLength) {
      const el = this.$refs.chartRef;
      const cw = el?.clientWidth || window.innerWidth || 430;
      const isNarrow = cw < 900;
      const count = Math.max(chartDataLength, 1);
      const barMax = isNarrow
        ? Math.max(10, Math.floor((cw - 36) / count * 0.65))
        : null;
      return { cw, isNarrow, isPc: cw >= 1280, barMax };
    },
    getPickRole(horseNumber) {
      const n = Number(horseNumber);
      if (!Number.isFinite(n) || n <= 0) return '';
      const bankers = (this.highlightBankers || []).map(Number);
      const picks = (this.highlightPicks || []).map(Number);
      if (bankers.includes(n)) return 'banker';
      if (picks.includes(n)) return 'leg';
      return '';
    },
    barPickPalette(role) {
      if (role === 'banker') {
        return { baseColor: '#FFE082', labelColor: '#E65100' };
      }
      if (role === 'leg') {
        return { baseColor: '#C8E6C9', labelColor: '#1B5E20' };
      }
      return { baseColor: '#ffe4bc', labelColor: '#666' };
    },
    
    /** 选码高亮变化：立即重绘柱图（勿等 chartReady，避免按 C 清除后延迟数秒） */
    applyPickHighlightToChart() {
      if (!this.chart) return;
      this.updateChartData();
    },

    updateChartData(customData = null) {
      if (!this.chart || (!this.data && !customData)) return;

      const sourceData = customData || this.data;
     // console.log('源数据:', sourceData);
      
      let chartData;
      if (this.isOvernightActive) {
          // 隔夜模式：直接从源数据中找出最新的隔夜数据
          chartData = sourceData
              .map(item => {
                  // 找出所有隔夜时间点
                  const overnightKeys = Object.keys(item.time_series || {})
                      .filter(key => {
                          const minute = parseInt(key.split('_')[1]);
                          return minute > 720;
                      })
                      .sort((a, b) => {
                          // 按时间点从大到小排序
                          const minuteA = parseInt(a.split('_')[1]);
                          const minuteB = parseInt(b.split('_')[1]);
                          return minuteB - minuteA;
                      });

                  if (overnightKeys.length === 0) return null;

                  // 获取最新的隔夜数据
                  const latestKey = overnightKeys[0];
                  const timeData = item.time_series[latestKey];
                //  console.log('马匹', item.number, '隔夜数据:', timeData);

                  return {
                      number: item.number,
                      value: Number(timeData?.value) || 0,
                      // 隔夜模式下不使用 add_value
                      addValue: 0,
                      winOdds: Number(timeData?.win_odds) || Number(item.odds?.win) || 0
                  };
              })
              .filter(item => item && item.number != null)
              .sort((a, b) => a.winOdds - b.winOdds);
      } else {
          // 普通模式：使用当前时间点
          const timePoint = this.timeKeyForMinute(this.view_minute);
          chartData = sourceData
              .map(item => {
                  const timeData = item.time_series?.[timePoint];
                  return {
                      number: item.number,
                      value: Number(timeData?.value) || 0,
                      addValue: Number(timeData?.add_value) || 0,
                      winOdds: Number(timeData?.win_odds) || Number(item.odds?.win) || 0
                  };
              })
              .filter(item => item.number != null && item.number !== '')
              .sort((a, b) => (Number(a.winOdds) || 999) - (Number(b.winOdds) || 999));
      }

      if (!chartData.length) {
        this.chartBubbleItems = [];
        timelineDebug('chart_empty', { view_minute: this.view_minute, horses: sourceData?.length });
        this.chartReady = false;
        this.chartUpdating = true;
        try {
          this.chart.clear();
        } catch (e) { /* ignore */ }
        this.chartUpdating = false;
        this.lastResizeW = 0;
        this.lastResizeH = 0;
        return;
      }

      if (this.showDbBubbles) {
        this.chartBubbleItems = chartData.map((row) => {
          const horse = sourceData.find((h) => Number(h.number) === Number(row.number));
          return horse
            ? buildChartDbBubbleItem(horse, this.view_minute, this.isOvernightActive)
            : { number: row.number, upText: '', downText: '', upBg: 'transparent', downBg: 'transparent', downChangeStyle: {} };
        });
      } else {
        this.chartBubbleItems = [];
      }

      const { isNarrow, isPc, barMax } = this.getChartLayoutMetrics(chartData.length);
      const hideYAxis = this.compactLayout || this.pluginEmbed;
      const fontSize = isPc ? 14 : (isNarrow ? 9 : 11);
      const labelSize = isPc ? 12 : (isNarrow ? 8 : 9);
      const lineHeight = isPc ? 20 : (isNarrow ? 14 : 16);

      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          }
        },
        grid: {
          top: hideYAxis
            ? (this.pluginEmbed ? (this.showDbBubbles ? 6 : 4) : 8)
            : (isNarrow ? 14 : '5%'),
          left: hideYAxis ? 4 : (isNarrow ? 32 : '3%'),
          right: hideYAxis ? 4 : (isNarrow ? 6 : '3%'),
          bottom: hideYAxis
            ? (this.pluginEmbed ? (this.showDbBubbles ? 38 : 34) : 48)
            : (isNarrow ? 52 : '12%'),
          containLabel: !hideYAxis,
          show: true,
          borderColor: '#333',
          borderWidth: 1
        },
        xAxis: {
          type: 'category',
          data: chartData.map(item => item.number),
          axisLabel: {
            interval: 0,
            hideOverlap: false,
            formatter: (value, index) => {
              const row = chartData[index];
              if (!row) return String(value ?? '');
              const odds = row.winOdds;
              const formattedOdds = odds < 10 ? odds.toFixed(1) : Math.round(odds);
              const role = this.getPickRole(row.number);
              const numTag = role === 'banker' ? 'bankerNum' : (role === 'leg' ? 'legNum' : 'num');
              return `{${numTag}|${value}}\n{odds|${formattedOdds}}`;
            },
            rich: {
              num: { fontSize, lineHeight, fontWeight: 'bold', color: '#111' },
              odds: { fontSize: labelSize, lineHeight, fontWeight: 'normal', color: '#888' },
              bankerNum: { fontSize, lineHeight, fontWeight: 'bold', color: '#F57F17' },
              legNum: { fontSize, lineHeight, fontWeight: 'bold', color: '#2E7D32' },
            },
            margin: hideYAxis && this.showDbBubbles ? 2 : (isNarrow ? 4 : 8),
          }
        },
        yAxis: {
          type: 'value',
          show: !hideYAxis,
          interval: 20,
          axisLabel: { show: !hideYAxis, fontSize },
          axisTick: { show: !hideYAxis },
          axisLine: { show: !hideYAxis },
          splitLine: { show: !hideYAxis },
        },
        series: [
          {
            name: '基礎值',
            type: 'bar',
            stack: 'total',
            data: chartData.map(item => {
              const pal = this.barPickPalette(this.getPickRole(item.number));
              return {
              value: item.value,
              itemStyle: {
                color: pal.baseColor,
                borderColor: '#d4c4a8',
                borderWidth: 1,
              },
              label: {
                show: true,
                position: 'insideBottom',
                fontSize: labelSize,
                color: pal.labelColor,
                distance: 4
              }
            };
            }),
            barWidth: isNarrow ? undefined : '50%',
            barMaxWidth: barMax || undefined,
            barCategoryGap: isNarrow ? '25%' : '20%',
            z: 2
          },
          {
            name: '增量值',
            type: 'bar',
            stack: 'total',
            barMaxWidth: barMax || undefined,
            data: chartData.map(item => {
              const addNeg = item.addValue < 0;
              return {
              value: Math.abs(item.addValue),
              itemStyle: {
                color: addNeg ? '#cccccc' : '#ff6b6b',
                borderColor: addNeg ? '#cccccc' : '#ff6b6b',
                borderWidth: 1,
              },
              label: {
                show: false
              }
            };
            }),
            z: 1
          }
        ]
      };

      this.chartUpdating = true;
      this.chartReady = false;
      try {
        this.chart.setOption(option, { notMerge: true, replaceMerge: ['series', 'xAxis', 'yAxis'] });
        this.chartReady = true;
        this.lastResizeW = 0;
        this.lastResizeH = 0;
      } catch (err) {
        console.error('[HorseChart] setOption 失败', err, {
          view_minute: this.view_minute,
          len: chartData.length,
        });
      } finally {
        this.chartUpdating = false;
      }
      this.$nextTick(() => {
        requestAnimationFrame(() => this.resizeChart());
      });
    },
    timeKeyForMinute,
    clampViewMinute(target) {
      return clampViewMinuteLogic(target, this.liveMinute, this.availableTimePoints);
    },
    clampViewMinuteAtEntry(target) {
      return clampViewMinuteLogic(target, this.liveMinute, this.availableTimePoints, {
        capToLive: false,
      });
    },
    defaultViewMinute() {
      return defaultViewMinuteOnEntry(this.liveMinute, this.availableTimePoints, {
        raceCompleted: this.isRaceCompleted,
        syncFrontier: inferRaceViewXSyncMinute(this.data),
        syncFollow: this.isSyncFollowMode,
      });
    },
    logTimelineState(tag) {
      timelineDebug(tag, {
        race_id: this.race_id,
        api_current_minute: this.current_minute,
        live_minute: this.liveMinute,
        view_minute: this.view_minute,
        view_source: this.view_source,
        race_completed: this.isRaceCompleted,
        user_scrubbed: this.userHasScrubbedTimeline,
        entry_applied: this.entryMinuteApplied,
        sync_follow: this.isSyncFollowMode,
        available_points: this.availableTimePoints,
        default_minute: this.defaultViewMinute(),
        slider_index: this.sliderIndex,
        slider_max_index: this.sliderMaxIndex,
      });
    },
    liveViewMinute() {
      return this.clampViewMinute(this.liveMinute);
    },
    /** 是否处于「跟直播」位置（与直播缘同分钟） */
    isAtLiveView() {
      return minutesEqual(this.view_minute, this.resolveViewMinuteForLive());
    },
    /** 父组件采样：刷新前是否应跟直播缘（未手动回看且已在 live） */
    isTimelineAtLiveFollowEdge() {
      return !this.userHasScrubbedTimeline && this.isAtLiveView();
    },
    /** 调试/模拟直播：滑块贴 sync 缘并开启跟播 */
    goToLiveFollowEdge() {
      if (!this.data?.length || this.isOvernightActive) return;
      this.userHasScrubbedTimeline = false;
      this.liveFollowEnabled = true;
      this.isSyncFollowMode = true;
      const live = this.resolveViewMinuteForLive();
      this.view_minute = live;
      this.logTimelineState('snap_live_edge');
      this.updateChartData();
      this.emitViewMinuteChange();
    },
    getTimelineDebugSnapshot() {
      return {
        view_minute: this.view_minute,
        live_minute: this.liveMinute,
        current_minute: this.current_minute,
        user_scrubbed: this.userHasScrubbedTimeline,
        live_follow: this.liveFollowEnabled,
        sync_follow: this.isSyncFollowMode,
        at_live_edge: this.isAtLiveView(),
      };
    },
    /** 用户拖动滑块/按钮后更新跟播状态 */
    refreshScrubbedStateAfterUserMove() {
      const atEarliest = minutesEqual(this.view_minute, this.minViewMinute);
      if (atEarliest && this.isRaceCompleted) {
        this.isSyncFollowMode = false;
        this.liveFollowEnabled = false;
        this.userHasScrubbedTimeline = true;
        return;
      }
      if (this.isAtLiveView()) {
        this.isSyncFollowMode = true;
        this.liveFollowEnabled = true;
        this.userHasScrubbedTimeline = false;
        return;
      }
      this.isSyncFollowMode = false;
      this.liveFollowEnabled = false;
      this.userHasScrubbedTimeline = true;
    },
    /**
     * 消息触发 getview：跟播时 view 跟 current_minute 左移；仅用户主动向右回看时不跟
     * @param {boolean} wasAtLiveEdge 须在 payload 写入前采样
     */
    applyLiveRefresh({ wasAtLiveEdge = false } = {}) {
      if (this.isRaceCompleted || this.isOvernightActive || !this.data?.length) return;
      const live = this.liveViewMinute();
      const target = this.resolveViewMinuteForLive();

      if (this.userHasScrubbedTimeline) {
        this.liveFollowEnabled = false;
        this.isSyncFollowMode = false;
        this.lastLiveMinute = live;
        this.logTimelineState('follow_skipped_user_scrub');
        return;
      }

      const shouldFollow = wasAtLiveEdge || this.liveFollowEnabled || this.isSyncFollowMode;
      if (shouldFollow) {
        if (!minutesEqual(this.view_minute, target)) {
          this.view_minute = target;
          this.liveFollowEnabled = true;
          this.isSyncFollowMode = true;
          this.userHasScrubbedTimeline = false;
          this.logTimelineState('follow_live_refresh');
          this.updateChartData();
          this.emitViewMinuteChange();
        }
        this.lastLiveMinute = live;
        return;
      }

      if (this.view_minute > live + 0.001) {
        this.lastLiveMinute = live;
        this.logTimelineState('review_right_of_live_no_follow');
        return;
      }

      this.lastLiveMinute = live;
    },
    /** getview 刷新：跟播模式下 view_minute 跟 current_minute */
    followLiveAfterRefresh() {
      if (this.isRaceCompleted || this.isOvernightActive || !this.data?.length) return;
      if (this.userHasScrubbedTimeline) {
        this.logTimelineState('follow_skipped_user_scrub');
        return;
      }
      if (!this.liveFollowEnabled && !this.isSyncFollowMode) {
        this.logTimelineState('follow_skipped_not_enabled');
        return;
      }
      const live = this.liveViewMinute();
      const target = this.resolveViewMinuteForLive();

      if (!minutesEqual(this.view_minute, target)) {
        this.view_minute = target;
        this.isSyncFollowMode = true;
        this.liveFollowEnabled = true;
        this.logTimelineState('follow_live_refresh');
        this.updateChartData();
        this.emitViewMinuteChange();
      }

      this.lastLiveMinute = live;
    },
    /** 换场/首次 getview：默认最左 entry；测试直播则贴 current_minute */
    applyRaceEntry() {
      if (!this.data?.length || this.isOvernightActive) return;
      if (this.testLiveBoot) {
        this.applyTestLiveEntry();
        return;
      }
      this.userHasScrubbedTimeline = false;
      this.isSyncFollowMode = false;
      this.liveFollowEnabled = false;
      const entry = this.resolveEntryMinute();
      const points = this.availableTimePoints;
      let v = entry;
      if (points.length) {
        const idx = minuteIndex(points, entry);
        v = points[idx >= 0 ? idx : 0];
      }
      this.view_minute = v;
      this.entryMinuteApplied = true;
      const rid = String(this.race_id || '');
      if (rid) this.bootstrappedRaceId = rid;
      this.lastLiveMinute = this.liveViewMinute();
      this.logTimelineState('race_entry');
      this.updateChartData();
      this.emitViewMinuteChange();
    },
    /** getview_test 首次：滑块贴 API current_minute 并开启跟播 */
    applyTestLiveEntry() {
      if (!this.data?.length || this.isOvernightActive) return;
      this.userHasScrubbedTimeline = false;
      this.isSyncFollowMode = true;
      this.liveFollowEnabled = true;
      const v = this.resolveViewMinuteForLive();
      this.view_minute = v;
      this.entryMinuteApplied = true;
      const rid = String(this.race_id || '');
      if (rid) this.bootstrappedRaceId = rid;
      this.lastLiveMinute = this.liveViewMinute();
      this.logTimelineState('test_live_entry');
      this.updateChartData();
      this.emitViewMinuteChange();
    },
    /** 在 time_points 上解析 current_minute 对应刻度 */
    resolveViewMinuteForLive() {
      const live = this.liveViewMinute();
      const points = this.availableTimePoints;
      if (!points.length) return live;
      const exact = minuteIndex(points, live);
      if (exact >= 0 && minutesEqual(points[exact], live)) {
        return points[exact];
      }
      const inLive = points.filter((p) => p >= -1 && p <= 20);
      if (!inLive.length) return points[0];
      let best = inLive[0];
      let bestDiff = Math.abs(best - live);
      for (const p of inLive) {
        const d = Math.abs(p - live);
        if (d < bestDiff) {
          bestDiff = d;
          best = p;
        }
      }
      return best;
    },
    /** 插件 refresh：允许跟 getview current_minute */
    enableLiveFollow() {
      if (this.isRaceCompleted || this.isOvernightActive || !this.data?.length) return;
      this.liveFollowEnabled = true;
      if (!this.userHasScrubbedTimeline) {
        this.isSyncFollowMode = true;
      }
      this.followLiveAfterRefresh();
    },
    /** 供父组件在 getview 刷新后调用：柱图 timeline 跟频谱直播缘 */
    syncLiveTimeline() {
      if (this.isRaceCompleted) {
        this.applyCompletedRaceView();
        return;
      }
      this.enableLiveFollow();
    },
    /** 首次进入：父组件写入的 entry_minute；否则 time_points 最左 */
    resolveEntryMinute() {
      if (this.entry_minute != null && !Number.isNaN(Number(this.entry_minute))) {
        return Number(this.entry_minute);
      }
      const points = this.availableTimePoints;
      if (points.length) {
        return getMinViewMinute(points);
      }
      return raceViewXEntryMinute(this.data);
    },
    applyCompletedRaceView() {
      if (!this.data?.length || this.isOvernightActive) return;
      if (this.userHasScrubbedTimeline) return;
      this.isSyncFollowMode = false;
      this.liveFollowEnabled = false;
      this.userHasScrubbedTimeline = false;
      const points = this.availableTimePoints;
      const min = this.clampViewMinuteAtEntry(
        points.length ? getMinViewMinute(points) : this.resolveEntryMinute(),
      );
      if (!minutesEqual(this.view_minute, min)) {
        this.view_minute = min;
        this.logTimelineState('completed_race_view');
        this.updateChartData();
        this.emitViewMinuteChange();
      }
    },
    applyUserViewMinute(newValue) {
      const v = this.clampViewMinuteAtEntry(newValue);
      this.view_minute = v;
      this.refreshScrubbedStateAfterUserMove();
      this.logTimelineState('user_move');
      this.updateChartData();
      this.emitViewMinuteChange();
      return v;
    },
    handleSliderChange(event) {
      const points = this.availableTimePoints;
      const idx = Number(event.target.value);
      if (points[idx] != null) {
        this.applyUserViewMinute(points[idx]);
      }
    },
    handlePrev() {
      const points = this.availableTimePoints;
      const idx = minuteIndex(points, this.view_minute);
      if (idx > 0) {
        this.applyUserViewMinute(points[idx - 1]);
      }
    },
    handleNext() {
      const points = this.availableTimePoints;
      const idx = minuteIndex(points, this.view_minute);
      if (idx >= 0 && idx < points.length - 1) {
        this.applyUserViewMinute(points[idx + 1]);
      }
    },
    handleOvernight() {
      this.isOvernightActive = !this.isOvernightActive;
      if (this.isOvernightActive) {
        console.log('切换到隔夜模式');
        this.view_minute = 999;  // 设置一个特殊值表示隔夜模式
        this.updateChartData();
        this.emitViewMinuteChange();
      } else {
        console.log('恢复正常模式');
        this.view_minute = this.defaultViewMinute();
        this.updateChartData();
        this.emitViewMinuteChange();
      }
    },
    /** 仅换场/首次有数据时设默认分钟；与 handleResize、initChart 无关 */
    tryApplyEntryMinute() {
      if (this.entryMinuteApplied || !this.data?.length || this.isOvernightActive) return;
      if (this.userHasScrubbedTimeline) return;
      if (this.testLiveBoot) {
        this.applyTestLiveEntry();
      } else {
        this.applyRaceEntry();
      }
    },
    clampViewIfNeeded() {
      if (this.userHasScrubbedTimeline) return;
      const live = this.liveViewMinute();
      const clamped = this.clampViewMinute(this.view_minute);
      if (!minutesEqual(clamped, this.view_minute)) {
        this.view_minute = clamped;
        if (this.view_minute > live + 0.001) {
          this.view_minute = live;
        }
        this.userHasScrubbedTimeline = false;
        this.updateChartData();
        this.emitViewMinuteChange();
      }
    },
    /** 即场：跟直播缘（由 followLiveAfterRefresh 统一调度；此处供 watch 补刀） */
    syncViewToLiveIfNeeded() {
      this.followLiveAfterRefresh();
    },
    /** 换场时仅清状态；默认分钟等新场次 data 到达后再 apply */
    resetTimelineForRace() {
      this.userHasScrubbedTimeline = false;
      this.isSyncFollowMode = false;
      this.lastLiveMinute = null;
      this.bootstrappedRaceId = '';
      this.entryMinuteApplied = false;
      this.liveFollowEnabled = false;
      if (this.isOvernightActive) {
        this.isOvernightActive = false;
      }
    },
    emitViewMinuteChange() {
      this.$emit('view-minute-change', {
        view_minute: this.view_minute,
        isOvernightActive: this.isOvernightActive,
      });
      this.notifyViewMinute();
    },
    notifyViewMinute() {
      if (window.parent === window) return;
      window.parent.postMessage({
        type: 'windata_tyq_view_minute',
        view_minute: this.isOvernightActive ? 999 : this.view_minute,
        race_id: this.race_id != null ? String(this.race_id) : '',
      }, '*');
    },
  },
  mounted() {
    this.initChart();
    this.$nextTick(() => this.tryApplyEntryMinute());
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
    if (this.chart) {
      this.chart.dispose();
      this.chart = null;
    }
    this.chartReady = false;
  },
  watch: {
    race_id(newId, oldId) {
      if (String(newId || '') !== String(oldId || '')) {
        this.resetTimelineForRace();
        this.logTimelineState('race_id_change');
      }
    },
    data: {
      handler(val) {
        if (!val?.length) {
          this.bootstrappedRaceId = '';
          this.userHasScrubbedTimeline = false;
          this.entryMinuteApplied = false;
          return;
        }
        const rid = String(this.race_id || '');
        if (rid && rid !== this.bootstrappedRaceId) {
          this.logTimelineState('race_data_ready');
          if (!this.entryMinuteApplied) {
            if (this.testLiveBoot) {
              this.applyTestLiveEntry();
            } else {
              this.applyRaceEntry();
            }
          }
          return;
        }
        if (!this.entryMinuteApplied) {
          return;
        }
        if (this.isRaceCompleted) {
          if (!this.userHasScrubbedTimeline) {
            this.applyCompletedRaceView();
          }
        } else if (this.liveFollowEnabled && !this.userHasScrubbedTimeline) {
          this.followLiveAfterRefresh();
        }
      },
      deep: true
    },
    current_minute() {
      if (this.isOvernightActive || !this.liveFollowEnabled || this.userHasScrubbedTimeline) return;
      this.logTimelineState('watch_api_minute');
      if (this.isRaceCompleted) {
        this.applyCompletedRaceView();
        return;
      }
      this.followLiveAfterRefresh();
    },
    liveMinute() {
      if (this.isOvernightActive || !this.entryMinuteApplied || !this.liveFollowEnabled || this.isRaceCompleted || this.userHasScrubbedTimeline) return;
      this.followLiveAfterRefresh();
    },
    race_completed(val) {
      if (!val) return;
      this.entryMinuteApplied = true;
      this.applyCompletedRaceView();
      this.logTimelineState('race_completed_reset');
    },
    highlightPicks: {
      handler() {
        this.applyPickHighlightToChart();
      },
      deep: true,
    },
    highlightBankers: {
      handler() {
        this.applyPickHighlightToChart();
      },
      deep: true,
    },
  }
}
</script>

<style scoped>
.chart-container {
  width: 100%;
  max-width: 100%;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: #fff;
  box-sizing: border-box;
}

.chart {
  flex: 1 1 0;
  width: 100% !important;
  max-width: 100% !important;
  min-width: 0 !important;
  min-height: 80px;
  height: 100%;
  box-sizing: border-box;
  display: block;
  overflow: hidden;
}

.chart-db-bubbles {
  flex: 0 0 auto;
  display: flex;
  flex-direction: row;
  align-items: stretch;
  justify-content: space-around;
  width: 100%;
  min-height: var(--tyq-db-bubble-h, 36px);
  padding: 0 4px;
  margin-top: var(--tyq-chart-bubble-gap, 2px);
  box-sizing: border-box;
  overflow: hidden;
  border-top: none;
}

.chart-db-bubble-col {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 2px;
}

.db-bubble-slot {
  flex: 0 0 auto;
  min-height: 17px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.db-bubble-slot--down {
  min-height: 17px;
}

.db-bubble--change {
  box-shadow: none;
}

.db-bubble {
  display: inline-block;
  font-size: 9px;
  line-height: 1.2;
  padding: 2px 4px;
  border-radius: 4px;
  color: #222;
  font-weight: 600;
  white-space: nowrap;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.06);
}

.slider-container {
  height: 40px;
  min-height: 40px;
  max-height: 40px;
  padding: 0 10px;
  border-top: 1px solid #eee;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-sizing: border-box;
}

.slider-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 0;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.time-display {
  min-width: 60px;
  text-align: right;
  padding-right: 10px;
  font-size: 14px;
  color: #333;
}

.time-slider {
  flex: 1 1 auto;
  min-width: 80px;
  max-width: 180px;
  width: 180px;
  position: relative;
  height: 20px;
  display: flex;
  align-items: center;
}

.slider-track {
  position: absolute;
  width: 100%;
  height: 4px;
  background: #eee;
  border-radius: 2px;
  overflow: hidden;
}

.slider-track-inactive {
  position: absolute;
  height: 100%;
  background: #ccc;
  left: 0;
  transition: width 0.3s;
}

.slider-track-active {
  position: absolute;
  height: 100%;
  background: #1890ff;
  transition: all 0.3s;
}

.slider {
  position: relative;
  width: 100%;
  height: 4px;
  -webkit-appearance: none;
  background: transparent;
  outline: none;
  z-index: 1;
}

.slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 20px;
  height: 20px;
  background: #fff;
  border: 2px solid #1890ff;
  border-radius: 50%;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.nav-button {
  padding: 5px;
  border: 1px solid #ddd;
  background: #fff;
  cursor: pointer;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  width: 28px;
  height: 28px;
  z-index: 2;
}

.nav-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.nav-button:hover:not(:disabled) {
  background: #f5f5f5;
  color: #333;
}

.overnight-button {
  padding: 5px 12px;
  border: 1px solid #ddd;
  background: #fff;
  cursor: pointer;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  height: 28px;
  font-size: 14px;
  margin-left: 8px;
  transition: all 0.2s;
}

.overnight-button:hover {
  background: #f5f5f5;
  color: #333;
  border-color: #999;
}

.overnight-button.active {
  background-color: #2c2c2c;
  color: #FFD700;
  border-color: #FFD700;
}

/* 移動端樣式 (包括 iPad Air) */
@media screen and (max-width: 1023px) {
  .chart {
    min-height: 250px;
  }

  .chart-container {
    min-height: 300px;
  }
}

:global(.container.tyq-plugin-iframe) .chart,
:global(.container.tyq-plugin-iframe) .chart-container,
:global(.container.tyq-narrow-layout) .chart,
:global(.container.tyq-narrow-layout) .chart-container {
  min-height: 0 !important;
}

/* PC端樣式 */
@media screen and (min-width: 1280px) {
  .chart {
    min-height: 300px;
  }

  .chart-container {
    min-height: 350px;
  }
}

/* 移动端适配 */
@media screen and (max-width: 430px) {
  .slider-wrapper {
    min-width: auto;
    gap: 4px;
  }
  
  .overnight-button {
    padding: 5px 8px;
    font-size: 12px;
    margin-left: 4px;
  }
}

.slider:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.slider-track-inactive,
.slider-track-active {
  opacity: 0.5;
  pointer-events: none;
}

/* 当处于隔夜模式时，整个滑块区域显示禁用状态 */
.time-slider:has(.slider:disabled) {
  opacity: 0.5;
  pointer-events: none;
}
</style>